"use client";

import * as React from "react";
import Link from "next/link";

import { cn } from "@rivval/css";
import {
  DotPattern,
  MenuIcon,
  RIVVALLogo,
  ShoppingCartIcon,
} from "@rivval/icons";
import { Button } from "@rivval/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@rivval/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@rivval/ui/sheet";
import VisuallyHidden from "@rivval/ui/visually-hidden";

import { SearchBar } from "~/lib/components/ui/search-bar";
import { DESKTOP_NAV } from "~/lib/config/site";

export function Navigation() {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <header className="fixed top-0 z-50 w-full backdrop-blur-xs">
      <div className="container flex h-20 items-center justify-between bg-black/50 px-4 shadow-lg shadow-black/50 lg:bg-transparent lg:shadow-none">
        {/* Mobile Navigation Trigger - Always Left */}
        <div className="xl:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-yellow-400">
                <MenuIcon className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <VisuallyHidden>
              <SheetTitle>RIVVAL</SheetTitle>
            </VisuallyHidden>
            <SheetContent
              side="left"
              className="relative w-[75vw] max-w-md border-r-2 border-yellow-400 bg-black/90 text-white"
            >
              <div className="absolute inset-0 h-full w-full overflow-hidden">
                <DotPattern className="h-full w-full" />
              </div>
              <div className="relative flex flex-col gap-4">
                <div className="flex items-center justify-center">
                  <Link
                    href="/"
                    className="flex items-center space-x-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <RIVVALLogo className="h-8 w-auto sm:h-10 md:h-10" />
                  </Link>
                </div>
                <nav className="flex flex-col gap-4">
                  {DESKTOP_NAV.map((item) => (
                    <React.Fragment key={item.title}>
                      {item.subNav ? (
                        <div className="flex flex-col gap-2">
                          <p className="text-lg font-medium tracking-wider text-cyan-400 uppercase">
                            {item.title}
                          </p>
                          <div className="flex flex-col gap-2 pl-4">
                            {item.subNav.map((subItem) => (
                              <Link
                                key={subItem.title}
                                href={subItem.href}
                                className="text-base tracking-wider text-white/80 uppercase transition-colors hover:text-yellow-400"
                                onClick={() => setIsOpen(false)}
                              >
                                {subItem.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          href={item.href}
                          className="text-lg text-yellow-400 uppercase hover:text-yellow-500"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.title}
                        </Link>
                      )}
                    </React.Fragment>
                  ))}
                  <SearchBar className="sm:hidden" />
                  <div className="fixed right-4 bottom-4 left-4 space-y-2">
                    <Button className="clip-nav w-full bg-yellow-400 font-bold tracking-wider text-black uppercase hover:bg-yellow-500">
                      Cart
                      <ShoppingCartIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Logo */}
        <Link
          href="/"
          className="absolute left-1/2 -translate-x-1/2 transform items-center space-x-2 xl:relative xl:left-0 xl:-translate-x-0 xl:transform-none"
        >
          <RIVVALLogo className="h-12 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden xl:flex">
          <div className="clip-nav-wrapper relative">
            <div className="clip-nav-border absolute inset-0 bg-yellow-400" />
            <NavigationMenuList
              className={cn(
                "clip-nav relative flex list-none items-center justify-center space-x-0 bg-black/90",
              )}
            >
              {DESKTOP_NAV.map((item) => (
                <NavigationMenuItem key={item.title} className="">
                  {item.subNav ? (
                    <>
                      <NavigationMenuTrigger className="px-8 py-4 tracking-wider text-white uppercase hover:text-yellow-400">
                        {item.title}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[600px] gap-3 border border-yellow-400 bg-black/90 p-6 backdrop-blur-xs lg:grid-cols-[.75fr_1fr]">
                          {item.subNav.map((subItem, subIdx) =>
                            subIdx === 0 ? (
                              <li key={subItem.title} className="row-span-3">
                                <NavigationMenuLink asChild>
                                  <Link
                                    href={subItem.href}
                                    className="group relative flex h-full w-full flex-col justify-end overflow-hidden rounded-md border border-yellow-400/10 bg-linear-to-b from-black/50 to-black p-6 no-underline outline-hidden transition-colors select-none hover:border-yellow-400"
                                  >
                                    <div className="absolute inset-0 z-10 bg-linear-to-b from-black/10 to-black" />
                                    {subItem.backgroundImage && (
                                      <div
                                        className="absolute inset-0 bg-cover bg-center opacity-50"
                                        style={{
                                          backgroundImage: `url('${subItem.backgroundImage}')`,
                                        }}
                                      />
                                    )}
                                    <div className="relative z-20 mt-4">
                                      <div className="mb-2 text-lg font-bold text-yellow-400">
                                        {subItem.title}
                                      </div>
                                      <p className="text-sm leading-tight text-white/70">
                                        {subItem.description}
                                      </p>
                                    </div>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ) : (
                              <ListItem
                                key={subItem.title}
                                title={subItem.title}
                                href={subItem.href}
                              >
                                {subItem.description}
                              </ListItem>
                            ),
                          )}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <NavigationMenuLink asChild>
                      <Link
                        href={item.href}
                        className="block px-8 tracking-wider text-white uppercase transition-colors hover:text-yellow-400"
                      >
                        {item.title}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              ))}
              <NavigationMenuItem className="bg-black/30">
                <Button
                  variant="outline"
                  className="border-0 bg-yellow-400 px-8 tracking-wider text-black uppercase hover:bg-yellow-500 hover:text-black"
                >
                  Cart
                </Button>
              </NavigationMenuItem>
            </NavigationMenuList>
          </div>
        </NavigationMenu>
      </div>
    </header>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none hover:text-yellow-400 focus:text-yellow-400",
            className,
          )}
          {...props}
        >
          <div className="text-sm leading-none font-medium tracking-wider uppercase">
            {title}
          </div>
          <p className="line-clamp-2 text-sm leading-snug tracking-wider text-white/70 uppercase">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";
